/**
 * Path utilities for Next.js routing
 * Replaces React Router's generatePath function
 */

/**
 * Generate a path by replacing route parameters
 * @param path - Path template with :param placeholders
 * @param params - Object with parameter values
 * @returns Generated path string or '#' if params are invalid
 *
 * @example
 * generatePath('/funds/:fundId', { fundId: '123' }) // '/funds/123'
 * generatePath('/funds/:fundId/tokens/:tokenId', { fundId: '123', tokenId: '456' }) // '/funds/123/tokens/456'
 * generatePath('/funds/:fundId', { fundId: undefined }) // '#' (invalid params)
 */
export function generatePath(
  path: string,
  params: Record<string, string | number | undefined> = {},
): string {
  let result = path

  for (const [key, value] of Object.entries(params)) {
    // Guard against undefined/null values - return '#' to indicate invalid path
    if (value === undefined || value === null || value === '') {
      return '#'
    }
    result = result.replace(`:${key}`, String(value))
  }

  return result
}

/**
 * Build a query string from an object
 * @param params - Object with query parameters
 * @returns Query string (without leading ?)
 */
export function buildQueryString(
  params: Record<string, string | number | boolean | undefined>,
): string {
  const searchParams = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  }

  return searchParams.toString()
}
